import { z } from 'zod';
import { reportSedMaterialDependencyManifestV1Schema } from './decisionAnalysis';

import {
  customBriefPreferencesSchema,
  jsonValueSchema,
} from './reportContractsV2';

export const REPORT_CONTENT_INPUT_SCHEMA_VERSION =
  'report-content-input-v1' as const;
export const REPORT_CONTENT_SNAPSHOT_SCHEMA_VERSION =
  'report-content-snapshot-v1' as const;
export const REPORT_CONTENT_REVISION_SCHEMA_VERSION =
  'report-content-revision-ref-v1' as const;
export const REPORT_CONTENT_CURRENT_SCHEMA_VERSION =
  'report-content-current-v1' as const;
export const REPORT_CONTENT_HASH_VERSION =
  'canonical-json-sha256-v1' as const;

export const reportContentProductSchema = z.literal('custom-brief');
export type ReportContentProduct = z.infer<typeof reportContentProductSchema>;

const nonEmptyString = z.string().trim().min(1);
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const isoDateString = z.string().datetime({ offset: true });

export const reportContentSedReferenceV1Schema = z.object({
  id: nonEmptyString,
  version: z.number().int().positive(),
  contentHash: sha256,
  /** Immutable full-document dependency proof; old references retain their exact policy. */
  materialDependencies: reportSedMaterialDependencyManifestV1Schema.optional(),
}).strict();

export const reportContentSnapshotReferenceV1Schema = z.object({
  contentHash: sha256,
  snapshot: jsonValueSchema,
}).strict();

export const reportContentInputV1Schema = z.object({
  schemaVersion: z.literal(REPORT_CONTENT_INPUT_SCHEMA_VERSION),
  hashVersion: z.literal(REPORT_CONTENT_HASH_VERSION),
  siteId: nonEmptyString,
  productId: reportContentProductSchema,
  site: reportContentSnapshotReferenceV1Schema,
  sed: reportContentSedReferenceV1Schema,
  projectIntent: reportContentSnapshotReferenceV1Schema,
  customBrief: z.object({
    contentHash: sha256,
    snapshot: customBriefPreferencesSchema,
  }).strict(),
  evidence: reportContentSnapshotReferenceV1Schema,
}).strict();

export type ReportContentInputV1 = z.infer<typeof reportContentInputV1Schema>;

export const reportContentReadinessV1Schema = z.object({
  state: z.enum(['ready', 'held', 'degraded', 'failed', 'stale']),
  ready: z.boolean(),
  current: z.boolean(),
  canView: z.boolean(),
  canCreateExport: z.boolean(),
  reason: z.string().nullable(),
  evaluatedAt: isoDateString,
}).strict().superRefine((readiness, ctx) => {
  const fullyReady = readiness.state === 'ready'
    && readiness.ready
    && readiness.current
    && readiness.canView
    && readiness.canCreateExport;
  if (readiness.state === 'ready' && !fullyReady) {
    ctx.addIssue({
      code: 'custom',
      path: ['state'],
      message: 'Ready report content must be current, viewable, and export-eligible.',
    });
  }
  if (readiness.state !== 'ready' && readiness.ready) {
    ctx.addIssue({
      code: 'custom',
      path: ['ready'],
      message: 'Only ready report content may set ready=true.',
    });
  }
  if (readiness.state !== 'ready' && !readiness.reason?.trim()) {
    ctx.addIssue({
      code: 'custom',
      path: ['reason'],
      message: 'Non-ready report content must explain why it is not ready.',
    });
  }
});

export type ReportContentReadinessV1 =
  z.infer<typeof reportContentReadinessV1Schema>;

export const reportContentSnapshotV1Schema = z.object({
  schemaVersion: z.literal(REPORT_CONTENT_SNAPSHOT_SCHEMA_VERSION),
  contentRevisionId: nonEmptyString,
  siteId: nonEmptyString,
  productId: reportContentProductSchema,
  reportId: nonEmptyString,
  reportRevisionId: nonEmptyString,
  input: reportContentInputV1Schema,
  inputHash: sha256,
  contentHash: sha256,
  createdAt: isoDateString,
}).strict();

export type ReportContentSnapshotV1 =
  z.infer<typeof reportContentSnapshotV1Schema>;

export const reportContentRevisionReferenceV1Schema = z.object({
  schemaVersion: z.literal(REPORT_CONTENT_REVISION_SCHEMA_VERSION),
  contentRevisionId: nonEmptyString,
  siteId: nonEmptyString,
  productId: reportContentProductSchema,
  reportId: nonEmptyString,
  reportRevisionId: nonEmptyString,
  inputHash: sha256,
  contentHash: sha256,
  snapshotPath: nonEmptyString,
  createdAt: isoDateString,
  readiness: reportContentReadinessV1Schema,
}).strict();

export type ReportContentRevisionReferenceV1 =
  z.infer<typeof reportContentRevisionReferenceV1Schema>;

export const reportContentRefreshStateSchema = z.enum([
  'queued',
  'refreshing',
  'ready',
  'stale',
  'held',
  'degraded',
  'failed',
]);
export type ReportContentRefreshState =
  z.infer<typeof reportContentRefreshStateSchema>;

export const reportContentCurrentPointerV1Schema = z.object({
  schemaVersion: z.literal(REPORT_CONTENT_CURRENT_SCHEMA_VERSION),
  siteId: nonEmptyString,
  productId: reportContentProductSchema,
  reference: reportContentRevisionReferenceV1Schema.optional(),
  latestAttemptReference: reportContentRevisionReferenceV1Schema.optional(),
  refreshState: reportContentRefreshStateSchema,
  refreshInputHash: sha256.nullable(),
  refreshJobId: nonEmptyString.nullable(),
  refreshRequestedAt: isoDateString.optional(),
  refreshUpdatedAt: isoDateString,
  refreshReason: z.string().nullable(),
  updatedAt: isoDateString,
}).strict();

export type ReportContentCurrentPointerV1 =
  z.infer<typeof reportContentCurrentPointerV1Schema>;
